<template>
  <div class="page-card mall-order-page">
    <div class="page-head">
      <h2 class="page-title">商城订单</h2>
      <div class="page-subtitle">管理商城实物商品发货、物流跟踪与售后退款</div>
    </div>

    <div class="toolbar">
      <div class="toolbar-left">
        <el-input
          v-model="query.keyword"
          placeholder="订单号/商品/收件人/手机号/快递单号"
          clearable
          style="width: 280px"
          @keyup.enter.native="fetchData"
        />
        <el-select v-model="query.orderStatus" clearable placeholder="订单状态" style="width: 130px">
          <el-option label="待发货" :value="2" />
          <el-option label="已发货" :value="3" />
          <el-option label="已完成" :value="4" />
          <el-option label="已取消" :value="5" />
        </el-select>
        <el-select v-model="query.productType" clearable placeholder="商品类型" style="width: 130px">
          <el-option label="虚拟服务" :value="1" />
          <el-option label="实物商品" :value="2" />
        </el-select>
        <el-select v-model="query.refundStatus" clearable placeholder="退款状态" style="width: 130px">
          <el-option label="无退款" :value="0" />
          <el-option label="已退款" :value="1" />
        </el-select>
        <el-select v-model="query.afterSaleStatus" clearable placeholder="售后状态" style="width: 130px">
          <el-option label="待审核" :value="1" />
          <el-option label="已拒绝" :value="2" />
        </el-select>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          value-format="yyyy-MM-dd"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          style="width: 260px"
        />
        <el-button type="primary" @click="fetchData">查询</el-button>
        <el-button @click="resetQuery">重置</el-button>
      </div>
    </div>

    <el-table v-loading="loading" :data="list" border>
      <el-table-column prop="orderNo" label="订单号" min-width="170" />
      <el-table-column label="商品" min-width="240">
        <template slot-scope="{ row }">
          <div class="product-cell">
            <img v-if="row.coverUrl" class="product-cover" :src="fullUrl(row.coverUrl)" alt="">
            <div class="product-info">
              <div class="product-title">{{ row.productTitle || '-' }}</div>
              <div class="product-sub">{{ row.productTypeText || '-' }} x{{ row.quantity || 1 }}</div>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="买家" min-width="150">
        <template slot-scope="{ row }">
          <div>{{ row.artistName || '-' }}</div>
          <div class="cell-sub">{{ row.artistMobile || '-' }}</div>
        </template>
      </el-table-column>
      <el-table-column label="实付金额" width="110" align="right">
        <template slot-scope="{ row }">¥{{ formatMoney(row.payableAmount) }}</template>
      </el-table-column>
      <el-table-column label="订单状态" width="100">
        <template slot-scope="{ row }">
          <el-tag :type="orderStatusTagType(row)">{{ row.orderStatusText || '-' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="售后" width="110">
        <template slot-scope="{ row }">
          <el-tag v-if="row.afterSaleStatus === 1" type="warning">{{ row.afterSaleStatusText }}</el-tag>
          <el-tag v-else-if="row.afterSaleStatus === 2" type="info">{{ row.afterSaleStatusText }}</el-tag>
          <span v-else class="cell-sub">-</span>
        </template>
      </el-table-column>
      <el-table-column label="退款" width="90">
        <template slot-scope="{ row }">
          <el-tag :type="row.refundStatus === 1 ? 'danger' : 'info'">{{ row.refundStatusText || '-' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="物流" min-width="160">
        <template slot-scope="{ row }">
          <template v-if="row.expressNo">
            <div>{{ row.expressCompany || '-' }}</div>
            <div class="cell-sub">{{ row.expressNo }}</div>
          </template>
          <span v-else class="cell-sub">-</span>
        </template>
      </el-table-column>
      <el-table-column prop="createdAt" label="下单时间" width="170" />
      <el-table-column label="操作" width="280" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" @click="openDetail(row)">详情</el-button>
          <el-button
            v-if="canShip(row)"
            type="text"
            @click="openShipDialog(row)"
          >发货</el-button>
          <el-button
            v-if="canRefund(row)"
            type="text"
            class="danger-text"
            @click="openRefundDialog(row)"
          >退款</el-button>
          <el-button
            v-if="canApproveAfterSale(row)"
            type="text"
            class="danger-text"
            @click="handleApproveAfterSale(row)"
          >同意退款</el-button>
          <el-button
            v-if="canApproveAfterSale(row)"
            type="text"
            @click="openRejectDialog(row)"
          >拒绝</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog title="订单详情" :visible.sync="detailVisible" width="920px">
      <div v-loading="detailLoading">
        <el-descriptions v-if="detail.id" title="订单信息" :column="2" border class="detail-section">
          <el-descriptions-item label="订单号">{{ detail.orderNo }}</el-descriptions-item>
          <el-descriptions-item label="下单时间">{{ detail.createdAt || '-' }}</el-descriptions-item>
          <el-descriptions-item label="订单状态">{{ detail.orderStatusText || '-' }}</el-descriptions-item>
          <el-descriptions-item label="支付状态">{{ detail.payStatusText || '-' }}</el-descriptions-item>
          <el-descriptions-item label="退款状态">{{ detail.refundStatusText || '-' }}</el-descriptions-item>
          <el-descriptions-item label="实付金额">¥{{ formatMoney(detail.payableAmount) }}</el-descriptions-item>
        </el-descriptions>

        <el-descriptions v-if="detail.id" title="买家信息" :column="2" border class="detail-section">
          <el-descriptions-item label="艺人姓名">{{ detail.artistName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="手机号">{{ detail.artistMobile || '-' }}</el-descriptions-item>
        </el-descriptions>

        <el-descriptions v-if="detail.id" title="商品信息" :column="2" border class="detail-section">
          <el-descriptions-item label="商品名称" :span="2">{{ detail.productTitle || '-' }}</el-descriptions-item>
          <el-descriptions-item label="商品类型">{{ detail.productTypeText || '-' }}</el-descriptions-item>
          <el-descriptions-item label="数量">{{ detail.quantity || 1 }}</el-descriptions-item>
          <el-descriptions-item label="商品金额">¥{{ formatMoney(detail.productAmount) }}</el-descriptions-item>
          <el-descriptions-item label="运费">¥{{ formatMoney(detail.freightAmount) }}</el-descriptions-item>
          <el-descriptions-item label="优惠">-¥{{ formatMoney(detail.discountAmount) }}</el-descriptions-item>
          <el-descriptions-item label="优惠券">{{ detail.couponName || '未使用' }}</el-descriptions-item>
        </el-descriptions>

        <el-descriptions
          v-if="detail.id && detail.productType === 2"
          title="收货信息"
          :column="1"
          border
          class="detail-section"
        >
          <el-descriptions-item label="收件人">{{ detail.receiverName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="联系电话">{{ detail.receiverMobile || '-' }}</el-descriptions-item>
          <el-descriptions-item label="收货地址">{{ detail.receiverAddress || '-' }}</el-descriptions-item>
        </el-descriptions>

        <el-descriptions
          v-if="detail.id && (detail.expressNo || detail.shippedAt)"
          title="物流信息"
          :column="2"
          border
          class="detail-section"
        >
          <el-descriptions-item label="快递公司">{{ detail.expressCompany || '-' }}</el-descriptions-item>
          <el-descriptions-item label="快递单号">{{ detail.expressNo || '-' }}</el-descriptions-item>
          <el-descriptions-item label="发货时间">{{ detail.shippedAt || '-' }}</el-descriptions-item>
          <el-descriptions-item label="完成时间">{{ detail.completedAt || '-' }}</el-descriptions-item>
        </el-descriptions>

        <el-descriptions
          v-if="detail.id && detail.afterSaleStatus"
          title="售后申请"
          :column="2"
          border
          class="detail-section"
        >
          <el-descriptions-item label="售后状态">{{ detail.afterSaleStatusText || '-' }}</el-descriptions-item>
          <el-descriptions-item label="申请类型">{{ detail.afterSaleTypeText || '-' }}</el-descriptions-item>
          <el-descriptions-item label="申请时间">{{ detail.afterSaleAppliedAt || '-' }}</el-descriptions-item>
          <el-descriptions-item label="申请原因" :span="2">{{ detail.afterSaleReason || '-' }}</el-descriptions-item>
          <el-descriptions-item v-if="detail.afterSaleAuditRemark" label="审核备注" :span="2">{{ detail.afterSaleAuditRemark }}</el-descriptions-item>
        </el-descriptions>

        <el-descriptions
          v-if="detail.id && detail.refundStatus === 1"
          title="退款信息"
          :column="2"
          border
          class="detail-section"
        >
          <el-descriptions-item label="退款金额">¥{{ formatMoney(detail.refundAmount) }}</el-descriptions-item>
          <el-descriptions-item label="退款时间">{{ detail.refundedAt || '-' }}</el-descriptions-item>
          <el-descriptions-item label="退款说明" :span="2">{{ detail.refundRemark || '-' }}</el-descriptions-item>
        </el-descriptions>

        <el-descriptions v-if="detail.id && detail.adminRemark" title="售后备注" :column="1" border class="detail-section">
          <el-descriptions-item label="备注">{{ detail.adminRemark }}</el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>

    <el-dialog title="订单发货" :visible.sync="shipVisible" width="520px" @closed="resetShipForm">
      <el-form ref="shipFormRef" :model="shipForm" :rules="shipRules" label-width="96px">
        <el-form-item label="订单号">
          <span>{{ shipForm.orderNo || '-' }}</span>
        </el-form-item>
        <el-form-item label="快递公司" prop="expressCompany">
          <el-input v-model="shipForm.expressCompany" placeholder="如：顺丰速运" maxlength="64" />
        </el-form-item>
        <el-form-item label="快递单号" prop="expressNo">
          <el-input v-model="shipForm.expressNo" placeholder="请填写快递单号" maxlength="64" />
        </el-form-item>
        <el-form-item label="售后备注">
          <el-input v-model="shipForm.adminRemark" type="textarea" :rows="3" maxlength="500" show-word-limit />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="shipVisible = false">取消</el-button>
        <el-button type="primary" :loading="shipSubmitting" @click="submitShip">确认发货</el-button>
      </span>
    </el-dialog>

    <el-dialog title="售后退款" :visible.sync="refundVisible" width="520px" @closed="resetRefundForm">
      <el-form ref="refundFormRef" :model="refundForm" :rules="refundRules" label-width="96px">
        <el-form-item label="订单号">
          <span>{{ refundForm.orderNo || '-' }}</span>
        </el-form-item>
        <el-form-item label="实付金额">
          <span>¥{{ formatMoney(refundForm.payableAmount) }}</span>
        </el-form-item>
        <el-form-item label="退款金额" prop="refundAmount">
          <el-input v-model="refundForm.refundAmount" placeholder="默认全额退款">
            <template slot="prepend">¥</template>
          </el-input>
        </el-form-item>
        <el-form-item label="退款说明" prop="refundRemark">
          <el-input v-model="refundForm.refundRemark" type="textarea" :rows="3" maxlength="500" show-word-limit placeholder="请填写退款原因" />
        </el-form-item>
        <el-form-item label="售后备注">
          <el-input v-model="refundForm.adminRemark" type="textarea" :rows="2" maxlength="500" show-word-limit />
        </el-form-item>
      </el-form>
      <div class="refund-tip">退款将原路退回买家邀请中心奖励余额，并恢复库存与优惠券（如有）。</div>
      <span slot="footer">
        <el-button @click="refundVisible = false">取消</el-button>
        <el-button type="danger" :loading="refundSubmitting" @click="submitRefund">确认退款</el-button>
      </span>
    </el-dialog>
    <el-dialog title="拒绝售后" :visible.sync="rejectVisible" width="520px" @closed="resetRejectForm">
      <el-form ref="rejectFormRef" :model="rejectForm" label-width="96px">
        <el-form-item label="订单号">
          <span>{{ rejectForm.orderNo || '-' }}</span>
        </el-form-item>
        <el-form-item label="申请类型">
          <span>{{ rejectForm.afterSaleTypeText || '-' }}</span>
        </el-form-item>
        <el-form-item label="申请原因">
          <span>{{ rejectForm.afterSaleReason || '-' }}</span>
        </el-form-item>
        <el-form-item label="审核备注">
          <el-input v-model="rejectForm.auditRemark" type="textarea" :rows="3" maxlength="500" show-word-limit placeholder="请填写拒绝原因" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="rejectVisible = false">取消</el-button>
        <el-button type="primary" :loading="rejectSubmitting" @click="submitReject">确认拒绝</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { Message, MessageBox } from 'element-ui'
import { API_BASE_URL } from '../../../utils/request'
import { hasPermission } from '../../../utils/auth'
import {
  approveMallOrderAfterSale,
  getMallOrderDetail,
  getMallOrders,
  refundMallOrder,
  rejectMallOrderAfterSale,
  shipMallOrder
} from '../../../api/mall'

export default {
  data() {
    return {
      loading: false,
      list: [],
      dateRange: [],
      query: {
        keyword: '',
        orderStatus: null,
        productType: null,
        refundStatus: null,
        afterSaleStatus: null
      },
      detailVisible: false,
      detailLoading: false,
      detail: {},
      shipVisible: false,
      shipSubmitting: false,
      shipForm: {
        id: '',
        orderNo: '',
        expressCompany: '',
        expressNo: '',
        adminRemark: ''
      },
      shipRules: {
        expressCompany: [{ required: true, message: '请填写快递公司', trigger: 'blur' }],
        expressNo: [{ required: true, message: '请填写快递单号', trigger: 'blur' }]
      },
      refundVisible: false,
      refundSubmitting: false,
      refundForm: {
        id: '',
        orderNo: '',
        payableAmount: 0,
        refundAmount: '',
        refundRemark: '',
        adminRemark: ''
      },
      refundRules: {
        refundRemark: [{ required: true, message: '请填写退款说明', trigger: 'blur' }]
      },
      rejectVisible: false,
      rejectSubmitting: false,
      rejectForm: {
        id: '',
        orderNo: '',
        afterSaleTypeText: '',
        afterSaleReason: '',
        auditRemark: ''
      }
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    hasPermission,
    formatMoney(value) {
      const amount = Number(value)
      return Number.isFinite(amount) ? amount.toFixed(2) : '0.00'
    },
    fullUrl(url) {
      if (!url) return ''
      if (url.startsWith('http')) return url
      if (url.startsWith('/uploads/')) {
        const uploadPath = url.replace('/uploads/', '')
        if (uploadPath.includes('/')) {
          return `${API_BASE_URL}${url}`
        }
        return `${API_BASE_URL}/api/common/file/${uploadPath}`
      }
      return `${API_BASE_URL}${url}`
    },
    buildQueryParams() {
      const params = { ...this.query }
      if (this.dateRange && this.dateRange.length === 2) {
        params.startDate = this.dateRange[0]
        params.endDate = this.dateRange[1]
      }
      return params
    },
    async fetchData() {
      this.loading = true
      try {
        this.list = await getMallOrders(this.buildQueryParams())
      } finally {
        this.loading = false
      }
    },
    resetQuery() {
      this.query = {
        keyword: '',
        orderStatus: null,
        productType: null,
        refundStatus: null,
        afterSaleStatus: null
      }
      this.dateRange = []
      this.fetchData()
    },
    orderStatusTagType(row) {
      if (!row) return 'info'
      if (row.refundStatus === 1) return 'danger'
      if (row.afterSaleStatus === 1) return 'warning'
      if (row.orderStatus === 2) return 'warning'
      if (row.orderStatus === 3) return ''
      if (row.orderStatus === 4) return 'success'
      return 'info'
    },
    canShip(row) {
      return hasPermission('mall_order:ship')
        && row
        && row.productType === 2
        && row.payStatus === 2
        && row.refundStatus !== 1
        && row.orderStatus === 2
    },
    canRefund(row) {
      return hasPermission('mall_order:refund')
        && row
        && row.payStatus === 2
        && row.refundStatus !== 1
        && row.orderStatus === 2
        && row.afterSaleStatus !== 1
    },
    canApproveAfterSale(row) {
      return hasPermission('mall_order:refund')
        && row
        && row.afterSaleStatus === 1
        && row.refundStatus !== 1
    },
    async openDetail(row) {
      this.detailVisible = true
      this.detailLoading = true
      this.detail = {}
      try {
        this.detail = await getMallOrderDetail(row.id)
      } finally {
        this.detailLoading = false
      }
    },
    openShipDialog(row) {
      this.shipForm = {
        id: row.id,
        orderNo: row.orderNo,
        expressCompany: '',
        expressNo: '',
        adminRemark: row.adminRemark || ''
      }
      this.shipVisible = true
      this.$nextTick(() => {
        if (this.$refs.shipFormRef) this.$refs.shipFormRef.clearValidate()
      })
    },
    resetShipForm() {
      this.shipForm = {
        id: '',
        orderNo: '',
        expressCompany: '',
        expressNo: '',
        adminRemark: ''
      }
    },
    submitShip() {
      this.$refs.shipFormRef.validate(async (valid) => {
        if (!valid) return
        this.shipSubmitting = true
        try {
          await shipMallOrder(this.shipForm.id, {
            expressCompany: this.shipForm.expressCompany,
            expressNo: this.shipForm.expressNo,
            adminRemark: this.shipForm.adminRemark
          })
          Message.success('发货成功')
          this.shipVisible = false
          this.fetchData()
        } finally {
          this.shipSubmitting = false
        }
      })
    },
    handleApproveAfterSale(row) {
      const typeText = row.afterSaleTypeText || '售后'
      MessageBox.confirm(`确认同意该订单的${typeText}申请？退款将退回买家奖励余额。`, '同意退款', { type: 'warning' })
        .then(async () => {
          await approveMallOrderAfterSale(row.id, {})
          Message.success('已同意退款')
          this.fetchData()
          if (this.detailVisible && this.detail.id === row.id) {
            this.openDetail(row)
          }
        })
        .catch(() => {})
    },
    openRejectDialog(row) {
      this.rejectForm = {
        id: row.id,
        orderNo: row.orderNo,
        afterSaleTypeText: row.afterSaleTypeText,
        afterSaleReason: row.afterSaleReason,
        auditRemark: ''
      }
      this.rejectVisible = true
    },
    resetRejectForm() {
      this.rejectForm = {
        id: '',
        orderNo: '',
        afterSaleTypeText: '',
        afterSaleReason: '',
        auditRemark: ''
      }
    },
    async submitReject() {
      this.rejectSubmitting = true
      try {
        await rejectMallOrderAfterSale(this.rejectForm.id, {
          auditRemark: this.rejectForm.auditRemark
        })
        Message.success('已拒绝售后申请')
        this.rejectVisible = false
        this.fetchData()
      } finally {
        this.rejectSubmitting = false
      }
    },
    openRefundDialog(row) {
      this.refundForm = {
        id: row.id,
        orderNo: row.orderNo,
        payableAmount: row.payableAmount,
        refundAmount: this.formatMoney(row.payableAmount),
        refundRemark: '',
        adminRemark: row.adminRemark || ''
      }
      this.refundVisible = true
      this.$nextTick(() => {
        if (this.$refs.refundFormRef) this.$refs.refundFormRef.clearValidate()
      })
    },
    resetRefundForm() {
      this.refundForm = {
        id: '',
        orderNo: '',
        payableAmount: 0,
        refundAmount: '',
        refundRemark: '',
        adminRemark: ''
      }
    },
    submitRefund() {
      this.$refs.refundFormRef.validate(async (valid) => {
        if (!valid) return
        const refundAmount = Number(this.refundForm.refundAmount)
        const payable = Number(this.refundForm.payableAmount)
        if (!Number.isFinite(refundAmount) || refundAmount <= 0) {
          Message.warning('请填写正确的退款金额')
          return
        }
        if (refundAmount > payable) {
          Message.warning('退款金额不能超过实付金额')
          return
        }
        MessageBox.confirm(`确认退款 ¥${refundAmount.toFixed(2)} 至买家奖励余额？`, '售后退款', { type: 'warning' })
          .then(async () => {
            this.refundSubmitting = true
            try {
              await refundMallOrder(this.refundForm.id, {
                refundAmount,
                refundRemark: this.refundForm.refundRemark,
                adminRemark: this.refundForm.adminRemark
              })
              Message.success('退款成功')
              this.refundVisible = false
              this.fetchData()
            } finally {
              this.refundSubmitting = false
            }
          })
          .catch(() => {})
      })
    }
  }
}
</script>

<style scoped>
.mall-order-page .page-head {
  margin-bottom: 16px;
}

.page-subtitle {
  margin-top: 6px;
  color: #909399;
  font-size: 13px;
}

.product-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.product-cover {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  object-fit: cover;
  background: #f5f7fa;
  flex-shrink: 0;
}

.product-title {
  color: #303133;
  line-height: 1.4;
}

.product-sub,
.cell-sub {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}

.detail-section {
  margin-bottom: 16px;
}

.danger-text {
  color: #f56c6c;
}

.refund-tip {
  margin: 0 0 8px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #fdf6ec;
  color: #e6a23c;
  font-size: 13px;
  line-height: 1.5;
}
</style>
